

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../services/api';
import { useToast } from "../components/ToastContext";
import PropTypes from 'prop-types';

const GetAllTemplatesDialog = ({ 
  open, 
  onClose, 
  onTemplateSelect,
  resourceId,
  selectedProvider 
}) => {
  const [loading, setLoading] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [updatingTemplates, setUpdatingTemplates] = useState(new Set());
  const toast = useToast();

  const userId = sessionStorage.getItem('userId');

  const fetchAllTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/Resources/GetResourceTemplatesbyResourceType/${resourceId}`, {
        headers: {
          "X-CORRELATION-ID": crypto.randomUUID(),
          accept: "application/json",
        },
      });

      if (response.status === 200) {
        const filteredTemplates = (response.data || []).filter(template => {
          const providerMap = {
            '1': 'Azure',
            '2': 'AWS', 
            '3': 'GCP'
          };
          
          const templateProvider = providerMap[template.cloudProvider] || template.cloudProvider;
          // Only show active templates from the selected provider
          return templateProvider === selectedProvider && template.isActive === true;
        });
        
        setAllTemplates(filteredTemplates);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && resourceId) {
      fetchAllTemplates();
    }
  }, [open, resourceId, selectedProvider]);

const handleTemplateClick = async (templateId, templateName) => {
  setLoading(true);
  try {
    const response = await api.get(`/api/Resources/GetResourceTemplateDetails/${templateId}`, {
      headers: {
        "X-CORRELATION-ID": crypto.randomUUID(),
        accept: "application/json",
      },
    });

    if (response.status === 200) {
      // Just pass the raw array - don't transform it here
      onTemplateSelect(response.data, templateName);
      onClose();
    }
  } catch (error) {
    console.error("Error loading template:", error);
    toast.error("Failed to load template");
  } finally {
    setLoading(false);
  }
};

  // Direct delete without confirmation popup
  const handleDeleteClick = async (templateId, templateName) => {
  setUpdatingTemplates(prev => new Set(prev).add(templateId));
  try {
    const response = await api.post(
      `/api/Resources/UpdateTemplateStatus`,
      { templateId, userId },
      { headers: { "X-CORRELATION-ID": crypto.randomUUID(), accept: "application/json", "Content-Type": "application/json" } }
    );

    if (response.status === 200 && (response.data === true || response.data === "true")) {
      // Optimistically update UI by removing deleted template from state
      setAllTemplates(prev => prev.filter(t => t.templateId !== templateId));
      toast.success(`Template "${templateName}" deleted successfully!`);
    } else {
      throw new Error('API returned unsuccessful response');
    }
  } catch (error) {
    console.error("Error deleting template:", error);
    toast.error(`Failed to delete template "${templateName}": ${error.message}`);
  } finally {
    setUpdatingTemplates(prev => {
      const newSet = new Set(prev);
      newSet.delete(templateId);
      return newSet;
    });
  }
};


  // Define columns for DataGrid
  const columns = [
    {
      field: 'templateName',
      headerName: 'Template Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => handleTemplateClick(params.row.templateId, params.row.templateName)}
          sx={{
            textTransform: 'none',
            color: 'primary.main',
            textDecoration: 'underline',
            '&:hover': {
              color: 'primary.dark',
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            }
          }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'createdByName',
      headerName: 'Created By',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 'N/A'}
                  </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          size="small" 
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Delete Template">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {updatingTemplates.has(params.row.templateId) ? (
              <CircularProgress size={20} />
            ) : (
              <IconButton
                onClick={() => handleDeleteClick(params.row.templateId, params.row.templateName)}
                color="error"
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Tooltip>
      ),
    },
  ];

  // Prepare rows for DataGrid
  const rows = allTemplates.map((template) => ({
    id: template.templateId,
    templateId: template.templateId,
    templateName: template.templateName,
    createdDate: template.createdDate,
    createdByName: template.createdByName,
    isActive: template.isActive,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
       <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold'}}>
        <Typography variant="h6">
          All Templates - {selectedProvider} 
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : allTemplates.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body1" color="textSecondary">
              No templates found for {selectedProvider} with Resource Type {resourceId}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
             key={allTemplates.length}
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              hideFooterSelectedRowCount 
              disableColumnMenu
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  borderBottom: '2px solid #e0e0e0',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f9f9f9',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GetAllTemplatesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTemplateSelect: PropTypes.func.isRequired,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedProvider: PropTypes.string.isRequired,
};
export default GetAllTemplatesDialog;
